import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';
import {
    EzObject,
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzSpinnerType } from '/public/webcomponents/spinner/EzSpinnerType.js';

/**
 * @class
 * @extends {EzStaticClass}
 * Static constants for use with EzSpinner and releated classes
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzSpinnerConstants } from '/public/webcomponents/spinner/EzSpinnerConstants.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access static field, properties, and methods only.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzSpinnerConstants extends EzStaticClass {
    /**
     * @static
     * @public @readonly @property
     * Gets the default spinner width constant: 172 (representing pixels)
     * @returns {number}
     */
    static get DEFAULT_SPINNER_WIDTH() {
        return 172;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default spinner height constant: 172 (representing pixels)
     * @returns {number}
     */
    static get DEFAULT_SPINNER_HEIGHT() {
        return 172;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the generic spinner id constant: 'EzClockerDefaultSpinnerId'
     * @returns {string}
     */
    static get GENERIC_SPINNER_ID() {
        return 'EzClockerDefaultSpinnerId';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default parent container id constant: 'body'
     * @returns {string}
     */
    static get DEFAULT_SPINNER_PARENT_CONTAINER_ID() {
        return 'body';
    }

    /**
     * @static
     * @public @property
     * Gets the default spinner objects object
     * @returns {object}
     */
    static get DEFAULT_SPINNER_OPTIONS() {
        const gearSvgEnumData = EzSpinnerType.ezEnumData(EzSpinnerType.GEAR_SVG);

        return {
            id: EzString.stringWithTextOrDefault(
                EzSpinnerType.ezSpinnerConfig(EzSpinnerType.LINE_SPINNER_SVG)?.id,
                EzSpinnerConstants.GENERIC_SPINNER_ID),
            spinner: EzObject.assignOrDefault(
                EzSpinnerType.ezSpinnerConfig(EzSpinnerType.LINE_SPINNER_SVG),
                {
                    id: EzSpinnerType.GEAR_SVG,
                    display: gearSvgEnumData.displayName,
                    type: gearSvgEnumData.spinnerType,
                    assetUrl: gearSvgEnumData.assetUrl,
                    maxWidth: gearSvgEnumData.attributes.width,
                    minWidth: gearSvgEnumData.attributes.width,
                    width: gearSvgEnumData.attributes.width,
                    height: gearSvgEnumData.attributes.height,
                    canResize: gearSvgEnumData.attributes.canResize
                }),
            message: EzSpinnerConstants.generateFunnyRandomSpinnerWaitMessage(),
            parentContainerId: EzSpinnerConstants.DEFAULT_SPINNER_PARENT_CONTAINER_ID
        };
    }

    /**
     * @static
     * @public @function
     * Generates a random funny spinner wait message
     * @returns {string}
     */
    static generateFunnyRandomSpinnerWaitMessage() {
        const funnySpinnerMessages = [
            // 0
            'Hold on just a bit longer (I was temporarily enamored by your work) ...',
            // 1
            'Will be done ASAP ...',
            // 2
            'Trying to figure out what to do ...',
            // 3
            'Just a few more seconds please ...',
            // 4
            'Working hard for you ...',
            // 5
            'Almost done...',
            // 6
            'Just a sec...',
            // 7
            'I\'m on it...',
            // 8
            'This would normally take days, but I\'ll get it done in just a few seconds...',
            // 9
            'How is your day going so far? I bet your day will get even better while you wait...',
            // 10
            'Please wait... really, PLEASE WAIT ...',
            // 11
            'Thanks for being so paitent with me! I\'m going as fast as my CPU will allow ...',
            // 12
            'If this seems to take for ever you should blame it on code written by AI ...',
            // 13
            'You will be amazed by how fast I get this done ...',
            // 14
            'Anyone remember the infinity spinner ...',
        ]

        // Select an index from 0 to 13
        const funnySpinnerMessageIndex = Math.floor(
            Math.random() * funnySpinnerMessages.length);

        return funnySpinnerMessageIndex < funnySpinnerMessages.length
            ? funnySpinnerMessages[funnySpinnerMessageIndex]
            : 'Please wait ...';
    }

    /**
     * @static
     * @public @function
     * Adds a funny prefix to the spinner's message
     * @param {undefined|null|string} message
     * Default: EzSpinnerConstants.generateFunnyRandomSpinnerWaitMessage()
     * @returns {string}
     */
    static generateFunnyWaitMessagePrefix(message = EzSpinnerConstants.generateFunnyRandomSpinnerWaitMessage()) {
        if (!EzString.hasText(message)) {
            return EzSpinnerConstants.generateFunnyRandomSpinnerWaitMessage();
        }

        const funnySpinnerPrefixMessages = [
            // 0
            '(Whistling) ',
            // 1
            '(Varoom) ',
            // 2
            '(Zip Zang Zoom) ',
            // 3
            '(Chugging) ',
            // 4
            '(Fidgeting) ',
            // 5
            '(Grrrrrrinding) ',
            // 6
            '(Churning away) ',
            // 7
            '(Digging in) ',
            // 8
            '(Splish and asplash) ',
            // 9
            '(Ding! Bonk! Ring!) ',
            // 10
            '(Tappity tap) ',
            // 11
            '(Yeehaw!) ',
            // 12
            '(Jumping to action) ',
            // 13
            '(Spinny Spin Spin) ',
            // 14
            '(Spinning a Hour Glass) ',
            // 15
            '(Spinning a Beach Ball) ',
            // 16
            '(Infinity Spinner!) ',
            // 17
            '(Crunching) ',
            // 18
            '(Calling the engineering team at ezClocker!) ',
        ]

        // Select an index from 0 to 13
        const funnySpinnerPrefixMessageIndex = Math.floor(
            Math.random() * funnySpinnerPrefixMessages.length);

        const funnyMessagePrefix = funnySpinnerPrefixMessageIndex < funnySpinnerPrefixMessages.length
            ? `${funnySpinnerPrefixMessages[funnySpinnerPrefixMessageIndex]} ${message}`
            : EzString.EMPTY;

        return EzString.hasText(funnyMessagePrefix) && !message.toLowerCase().includes(message.toLowerCase())
            ? `${funnyMessagePrefix} ${message}`
            : message;
    }

    /**
     * @public @constructor
     * DO NOT CREATE NEW INSTANCES OF EzSpinnerConsts
     * Access static methods, fields, and properties only.
     */
    constructor() {
        super();
    }
}